<template>
  <div class="login">
    <p class="title">{{ $t('Log In Securely') }}</p>
    <p class="small">{{ $t('To log in to your Eved account, you will need to set up multifactor authentication') }}.</p>
    <p class="small">{{ $t('You will be sent a code each time you log in from a new device') }}.</p>
    <form class="form" @submit.prevent="setupMfa" autocomplete="false">
      <div class="">
        <ed-radio
          name="mfa-option"
          :field-label="$t('How would you like to receive your code?')"
          :options="mfaOptions"
          v-model="mfaOption"
          track-by="id"
        >
          <div class="row col-md10" slot="phoneNumber">
            <em class="small">
              {{ $t('Provide your phone number below') }}
            </em>
          </div>
        </ed-radio>
        <div class="phoneInput">
          <vue-country-code :disabled="!mfaOption || mfaOption.preference !== 'SMS_MFA'" class="dropdown" @onSelect="onCountrySelect" :disabledFetchingCountry=true :enabledCountryCode=true :preferredCountries="['us', 'gb', 'cn']"/>
          <ed-input :disabled="!mfaOption || mfaOption.preference !== 'SMS_MFA'" maxlength="26" v-model="phone.number" :error="!validMfaSetup.phoneNumber && !phone.number" @change="updatePhone" />
        <div class="phoneHint">
          <span v-show="mfaOption && mfaOption.preference === 'SMS_MFA' && !isVerificationSuccessful" class="notVerified" title="Phone number is not verified, click next to verify"></span>
          <span v-show="mfaOption && mfaOption.preference === 'SMS_MFA' && isVerificationSuccessful" class="Verified" title="Phone number was verified, click next to login"></span>
        </div>
        </div>
      </div>
      <div class="text-right">
        <ed-button class="alt" button-class="btn btn-secondary" type="button" value="cancel" @click="cancel">
          {{ $t('Cancel') }}
        </ed-button>
        &nbsp;
        <ed-button button-class="btn btn-primary" type="submit" value="next" :disabled="!mfaOption">
          {{ $t('Next') }}
        </ed-button>
      </div>
    </form>

    <ed-modal title="" type="small"
      ref="CHECK_EMAIL_CODE"
      @close="$refs['CHECK_EMAIL_CODE'].show = false"
    >
      <div slot="footer"></div>
      <div slot="body">
        <p class="small">Please enter the code received via e-mail</p>
        <ed-input v-model="mfaChallenge.code" />
        <ed-button @click="verifySetup">{{ $t('Verify') }}</ed-button>
      </div>
    </ed-modal>

    <ed-modal title="" type="small"
      ref="CHECK_SMS_CODE"
      @close="$refs['CHECK_SMS_CODE'].show = false"
    >
      <div slot="footer"></div>
      <div slot="body">
        <p class="small">Please enter the code received via SMS</p>
        <ed-input v-model="mfaChallenge.code" />
        <ed-button @click="verifySetup">{{ $t('Verify') }}</ed-button>
      </div>
    </ed-modal>
  </div>
</template>

<script>

import User from '@/core/user'
import { navigateToV3 } from '@/core/legacy-auth/gotoV3'

const CHECK_EMAIL_CODE = 'CHECK_EMAIL_CODE'
const CHECK_SMS_CODE = 'CHECK_SMS_CODE'

export default {
  alias: 'mfasetup',
  data() {
    return {
      mfaOption: undefined,
      mfaOptions: [
        {
          label: ` Email: ${this.$user.email}`,
          id: CHECK_EMAIL_CODE,
          preference: 'CUSTOM_CHALLENGE',
          status: false
        },
        {
          label: ' Text message',
          id: CHECK_SMS_CODE,
          preference: 'SMS_MFA',
          status: false,
          additionalInfo: true,
          slotName: 'phoneNumber',
          additionalSetup: this.api.$.mfa.setPhone,
          verifySetup: this.api.$.mfa.checkPhone
        }
      ],
      phone: {
        countryCode: undefined,
        number: undefined
      },
      mfaSetup: {
        phoneNumber: undefined
      },
      validMfaSetup: {
        phoneNumber: true
      },
      mfaChallenge: {
        code: ''
      },
      validMfaChallenge: {
        code: true
      },
      isVerificationSuccessful: false
    }
  },
  methods: {
    async setupMfa() {
      if (this.isVerificationSuccessful || !this.mfaOption.verifySetup) {
        await this.api.post(this.api.$.mfa.setConfig, {
          type: this.mfaOption.preference
        })
        const user = User.get()
        navigateToV3.call(this, user.token)
        return
      }

      if (!this.mfaOption.additionalInfo || this.mfaOption.additionalInfo && this.mfaSetup[this.mfaOption.slotName]) {
        if (this.mfaOption.additionalSetup) {
          const r = await this.api.post(this.mfaOption.additionalSetup, this.mfaSetup)
          if (r && r.data && !r.data.Success) {
            global.alert(r.data.ErrorMessage || 'Something went wrong')
            return
          }
        }

        if (this.mfaOption.verifySetup) {
          this.$refs[this.mfaOption.id].show = true
        }
      }
      if (this.mfaOption.additionalInfo && !this.mfaSetup[this.mfaOption.slotName]) {
        this.validMfaSetup[this.mfaOption.slotName] = false
      }
    },
    async verifySetup() {
      try {
        this.mfaChallenge.code = this.mfaChallenge.code.trim()
        const r = await this.api.post(this.mfaOption.verifySetup, this.mfaChallenge)
        if (r && r.data && r.data.Success) {
          this.isVerificationSuccessful = true
          this.$refs['CHECK_SMS_CODE'].show = false
        } else {
          global.alert(r.data.ErrorMessage || 'Something went wrong')
        }
      } catch (e) {
        global.alert('Something went wrong')
      }
    },
    cancel() {
      this.$user.logout()
    },
    onCountrySelect({dialCode}) {
      this.phone.countryCode = '+' + dialCode
      this.updatePhone()
    },
    updatePhone() {
      if (this.phone.number !== undefined) {
        this.mfaSetup.phoneNumber = this.phone.countryCode + this.phone.number.replace(/[/(/)/ -/.]/g, '')
      }
      this.isVerificationSuccessful = false
    }
  }
}

</script>

<style lang='scss' scoped>

@import '~@/assets/sass/font-awesome/fontawesome-pro-regular.scss';
@import "~@/assets/sass/_variables.scss";

.login {
  padding: 0 5px;
}
.login >>> {
  .dawn-radio-option-content.additional {
    padding-bottom: 0;
    height: auto;
  }
}

.login .title {
  font-weight: $font-weight-extra-bold;
  margin-bottom: 1rem;
}
.login .navbar .navbar-brand.logo {
  img { display: inline; }
}

.login .phoneInput {
  padding-left: 12px;
  div.form-group{
    display: inline-block;
    padding-left: 8px;
    width: 70%;
  }
  >>> div.vue-country-select{
    border-radius: unset;
    height: 34px;
    width:90px;
    top: 2px;
    ul.dropdown-list{
      width: 345px;
    }
  }
  div.vue-country-select:focus-within{
    box-shadow: 0px 0px 0px 1px #a9d85f;
    border-color: #a9d85f;
  }
  >>> div.vue-country-select span.current>span.country-code{
    color: #333;
    font-weight: 400;
    font-size: 0.85rem;
    padding-left:8px !important;
  }
}

.login .phoneHint{
  display: block;
  position: absolute;
  left: 84%;
  top: 69%;
  z-index: 100;
  background: white;
  font: 20px 'Font Awesome 5 Regular';
  .notVerified:before { content: '\f06a'; }
  .Verified:before { 
    content: '\f00c'; 
    color: #a9d85f;
    }
}

.login-message {
  font-size: $font-size-base;
  line-height: 4.5em;
}

</style>



// WEBPACK FOOTER //
// src/components/Login/mfa-setup.vue